import { createSelector } from '@reduxjs/toolkit';

import { RelationshipMapping } from 'src/containers/ProjectDiagram/types/data';
import { LinkData } from 'src/containers/ProjectDiagram/types/model';
import { isRelationshipMapping } from 'src/containers/ProjectDiagram/utils/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import {
  prevProjectLinkDataArraySelector,
  projectLinkDataArraySelector,
} from 'src/selectors/projectLinkDataArray';

type RelationshipMappingFilter = {
  relationshipIri: string;
  sourceKey?: string;
  targetKey?: string;
  sourceIri?: string;
  targetIri?: string;
};

const filterRelationshipMappings =
  (filter?: RelationshipMappingFilter) => (linkDataArray: LinkData[]) =>
    linkDataArray.filter((linkData): linkData is RelationshipMapping => {
      // ignore things that are not relationship mappings
      if (!isRelationshipMapping(linkData)) {
        return false;
      }

      // no need to filter if there are no filters
      if (!filter) {
        return true;
      }

      // return early if the relationship iri does not match
      if (linkData.relationshipIri !== filter.relationshipIri) {
        return false;
      }

      if (
        filter.sourceIri &&
        // the source iri matches the relationship mapping identifier class iri
        (linkData.fromClassIri === filter.sourceIri ||
          // the source iri matches the linked source class mapping iri
          filter.sourceIri ===
            getModelConceptClassValue(
              linkDataArray.find(({ key }) => key === linkData.from)?.to || ''
            ))
      ) {
        return true;
      }

      if (
        filter.targetIri &&
        // the target iri matches the relationship mapping identifier class iri
        (linkData.toClassIri === filter.targetIri ||
          // the target iri matches the linked target class mapping iri
          filter.targetIri ===
            getModelConceptClassValue(
              linkDataArray.find(({ key }) => key === linkData.to)?.to || ''
            ))
      ) {
        return true;
      }

      // the source key matches the relationship mapping source
      if (filter.sourceKey && filter.sourceKey === linkData.from) {
        return true;
      }

      // the target key matches the relationship mapping target
      if (filter.targetKey && filter.targetKey === linkData.to) {
        return true;
      }

      // match if the relationship iri is the only filter
      return (
        !filter.sourceIri &&
        !filter.targetIri &&
        !filter.sourceKey &&
        !filter.targetKey
      );
    });

export const projectRelationshipMappingsSelector = (
  projectId: string,
  filter?: RelationshipMappingFilter
) =>
  createSelector(
    projectLinkDataArraySelector(projectId),
    filterRelationshipMappings(filter)
  );

export const prevProjectRelationshipMappingsSelector = (
  projectId: string,
  filter?: RelationshipMappingFilter
) =>
  createSelector(
    prevProjectLinkDataArraySelector(projectId),
    filterRelationshipMappings(filter)
  );
